import { useEffect, useRef, useState } from "react";
import { OpenSheetMusicDisplay } from "opensheetmusicdisplay";
import QuestionLayout from "./QuestionLayout";
import { Glyph } from "./staff/glyphs.jsx";
import { drawGlyphSVG } from "./score/drawGlyph";
import { missingRhythmGeometry, placedRhythmPosition, missingRhythmNoteY } from "./missingRhythmGeometry";

/* ───────────────────────────────────────────── */

export default function MissingRhythmWidget({
  id,
  config,
  value = {},
  onChange,
  mode,
  feedback
}) {
  const containerRef = useRef(null);
  const osmdRef = useRef(null);
  const layerRef = useRef(null);
  const initializedRef = useRef(false);
  const modeRef = useRef(mode);

  const [activeSlot, setActiveSlot] = useState(null);

  /* ───────── MODE REF ───────── */

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  /* ───────── OSMD INIT (ONCE, NO RESIZE EVER) ───────── */

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const osmd = new OpenSheetMusicDisplay(containerRef.current, {
      drawTitle: false,
      drawPartNames: false,
      drawMeasureNumbers: false,
      drawComposer: false,
      autoResize: false // 🔒 HARD LOCK
    });

    osmdRef.current = osmd;

    (async () => {
      await osmd.load(config.musicXML);

      // Keep the entire missing-rhythm excerpt on one system.
      // The fixed-width surface below will scroll horizontally when needed,
      // matching the behavior of the time-signature ID widget.
      const sourceMeasureCount = osmd.Sheet?.SourceMeasures?.length;
      const configuredMeasureCount = Math.max(
        1,
        ...(config.missingSlots ?? []).map(slot => Number(slot.measureIndex) + 1)
      );
      osmd.EngravingRules.RenderXMeasuresPerLineAkaSystem =
        sourceMeasureCount || configuredMeasureCount;

      osmd.render();

      // wait for layout + DOM paint
      await new Promise(r =>
        requestAnimationFrame(() =>
          requestAnimationFrame(r)
        )
      );

      ensureLayer();
      redraw();
    })();
  }, []);

  /* ───────── STATE REDRAWS ONLY ───────── */

  useEffect(() => {
    if (!initializedRef.current) return;
    redraw();
  }, [activeSlot, value, feedback, mode]);

  /* ───────── SVG LAYER ───────── */

  function ensureLayer() {
    const svg = containerRef.current?.querySelector("svg");
    if (!svg) return;

    const rootG = svg.querySelector("g");
    if (!rootG) return;

    let layer = rootG.querySelector("#missing-rhythm-layer");
    if (!layer) {
      layer = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "g"
      );
      layer.setAttribute("id", "missing-rhythm-layer");
      rootG.appendChild(layer);
    }

    layerRef.current = layer;
  }

  function clearLayer() {
    if (layerRef.current) {
      layerRef.current.innerHTML = "";
    }
  }

  /*
   * Placed rhythms use one fixed scale for the whole widget.  Do NOT
   * calibrate against the hidden source item: the source may be a different
   * duration/pitch, which made the same answer render differently by slot.
   */
  function drawPlacedRhythm({ layer, symbol, x, y, scale = 1 }) {
    const wrapper = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "g"
    );
    wrapper.setAttribute(
      "transform",
      `translate(${x}, ${y}) scale(${scale})`
    );
    layer.appendChild(wrapper);

    drawGlyphSVG({
      layer: wrapper,
      symbol,
      x: 0,
      y: 0
    });
    // Center rest shapes without using their height as a vertical anchor.
    const rest = symbol.endsWith("_rest");
    const bounds = rest ? wrapper.getBBox() : null;
    const offsetX = rest ? bounds.x + bounds.width / 2 : 5;
    wrapper.setAttribute("transform", `translate(${x - offsetX * scale}, ${y}) scale(${scale})`);
  }

  /* ───────── DRAW HELPERS ───────── */

  function drawArrow(x, y) {
    const g = document.createElementNS("http://www.w3.org/2000/svg", "g");

    const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
    line.setAttribute("x1", x);
    line.setAttribute("y1", y);
    line.setAttribute("x2", x);
    line.setAttribute("y2", y + 14);
    line.setAttribute("stroke", "#000");

    const tri = document.createElementNS("http://www.w3.org/2000/svg", "polygon");
    tri.setAttribute(
      "points",
      `${x - 4},${y + 10} ${x + 4},${y + 10} ${x},${y + 16}`
    );
    tri.setAttribute("fill", "#000");

    g.appendChild(line);
    g.appendChild(tri);
    layerRef.current.appendChild(g);
  }

  /* ───────── REDRAW ───────── */

  function redraw() {
    const osmd = osmdRef.current;
    const layer = layerRef.current;
    if (!osmd || !layer) return;

    clearLayer();

    const svg = containerRef.current.querySelector("svg");
    if (!svg) return;


    /* flatten measures */
    const measures = [];
    osmd.GraphicSheet.MusicPages.forEach(page =>
      page.musicSystems.forEach(system =>
        system.graphicalMeasures.forEach(group =>
          measures.push(group[0])
        )
      )
    );

    const missingEntries = new Set(config.missingSlots.map(
      slot => `${slot.measureIndex}:${slot.staffEntryIndex}`
    ));
    const entryIndices = new Map();
    const rhythmNotes = [];
    let entryIndex = 0;
    measures.forEach((measure, m) => {
      measure.staffEntries?.forEach((entry, s) => {
        const index = entryIndex++;
        entryIndices.set(`${m}:${s}`, index);
        if (missingEntries.has(`${m}:${s}`)) return;
        entry.graphicalVoiceEntries?.forEach(voice => {
          voice.notes?.forEach(note => {
            const vf = note.vfnote?.[0];
            if (!vf || vf.isRest?.()) return;
            const reference = missingRhythmGeometry(vf, vf.attrs?.el, layer);
            if (reference) rhythmNotes.push({ index, y: reference.noteY });
          });
        });
      });
    });

    config.missingSlots.forEach((slot, slotIndex) => {
      const measure = measures[slot.measureIndex];
      if (!measure) return;

      const staffEntry =
        measure.staffEntries?.[slot.staffEntryIndex];
      if (!staffEntry) return;

      const gve = staffEntry.graphicalVoiceEntries?.[0];
      const note = gve?.notes?.[0];
      const el = note?.vfnote?.[0]?.attrs?.el;
      if (!el) return;

      /* ALWAYS re-hide missing rhythm */
      el.style.visibility = "hidden";

      const geometry = missingRhythmGeometry(note.vfnote[0], el, layer);
      if (!geometry) return;
      if (note.vfnote[0].isRest?.()) {
        geometry.noteY = missingRhythmNoteY(
          geometry, rhythmNotes,
          entryIndices.get(`${slot.measureIndex}:${slot.staffEntryIndex}`)
        );
      }
      const cx = geometry.x;
      const staffTopY = geometry.topY;
      const highlightY = staffTopY - geometry.spacing;
      const highlightHeight = geometry.bottomY - staffTopY + 2 * geometry.spacing;

      /* ───── review highlight ───── */
      if (mode === "review" && feedback?.parts?.[slotIndex]) {
        const part = feedback.parts[slotIndex];

        const r = document.createElementNS("http://www.w3.org/2000/svg", "rect");
        r.setAttribute("x", cx - 20);
        r.setAttribute("y", highlightY);
        r.setAttribute("width", 40);
        r.setAttribute("height", highlightHeight);
        r.setAttribute("rx", 4);
        r.setAttribute(
          "fill",
          part.correct
            ? "rgba(46,125,50,0.15)"
            : "rgba(198,40,40,0.15)"
        );
        r.setAttribute(
          "stroke",
          part.correct ? "#2e7d32" : "#c62828"
        );
        layer.appendChild(r);
      }

      /* ───── active highlight ───── */
      if (mode !== "review" && activeSlot === slotIndex) {
        const r = document.createElementNS("http://www.w3.org/2000/svg", "rect");
        r.setAttribute("x", cx - 20);
        r.setAttribute("y", highlightY);
        r.setAttribute("width", 40);
        r.setAttribute("height", highlightHeight);
        r.setAttribute("rx", 4);
        r.setAttribute("fill", "rgba(0,122,255,0.15)");
        r.setAttribute("stroke", "#007aff");
        layer.appendChild(r);
      }

      /* ───── placed glyph ───── */
      if (value[slotIndex]) {
        const position = placedRhythmPosition(value[slotIndex], geometry, config.placedRhythmScale ?? 1);
        drawPlacedRhythm({
          layer,
          symbol: value[slotIndex],
          x: cx,
          y: position.y,
          scale: position.scale
        });
      }

      /* ───── arrow ───── */
      drawArrow(cx, staffTopY - 18);

      /* ───── hitbox ───── */
      const hit = document.createElementNS("http://www.w3.org/2000/svg", "rect");
      hit.setAttribute("x", cx - 18);
      hit.setAttribute("y", highlightY);
      hit.setAttribute("width", 36);
      hit.setAttribute("height", highlightHeight);
      hit.setAttribute("fill", "transparent");
      hit.setAttribute("cursor", "pointer");

      hit.addEventListener("click", e => {
        if (modeRef.current === "review") {
          e.stopPropagation();
          return;
        }
        setActiveSlot(slotIndex);
      });

      layer.appendChild(hit);
    });
  }

  /* ───────── ANSWER ───────── */

  function setAnswer(slotIndex, symbol) {
    onChange({ ...value, [slotIndex]: symbol });
    setActiveSlot(null);
  }

  const palette =
    config.allowedMissing ??
    ["whole_note", "dotted_half_note", "half_note", "quarter_note", "eighth_note"];

  /* ───────── RENDER ───────── */

  return (
    <QuestionLayout
      number={config.number}
      prompt={config.prompt}
      points={config.points}
      mode={mode}
      feedback={feedback}
    >
      <div
        style={{
          width: config.osmdWidth ?? 1400,
          maxWidth: "none"
        }}
      >
        <div
          ref={containerRef}
          style={{
            width: "100%",
            maxWidth: "none"
          }}
        />

        {activeSlot !== null && mode !== "review" && (
          <div
            style={{
              display: "flex",
              gap: 12,
              marginTop: 16,
              position: "sticky",
              left: 8,
              width: "max-content",
              zIndex: 2
            }}
          >
            {palette.map(symbol => (
              <div
                key={symbol}
                onClick={() => setAnswer(activeSlot, symbol)}
                style={{
                  padding: 8,
                  border: "1px solid #ccc",
                  backgroundColor: "#fff",
                  cursor: "pointer"
                }}
              >
                <svg width={72} height={72} viewBox="-35 -75 100 100">
                  <g transform={`translate(${paletteXOffset(symbol)}, ${paletteYOffset(symbol)}) scale(1.8)`}>
                    <RestStaffLine symbol={symbol} />
                    <Glyph symbol={symbol} />
                  </g>
                </svg>
              </div>
            ))}
          </div>
        )}
      </div>
    </QuestionLayout>
  );
}

function paletteXOffset(symbol) {
  if (symbol.indexOf("rest")!=-1) return 8;
  switch (symbol) {
    case "whole_rest":
      return 10;
    case "half_rest":
      return 6;
    case "quarter_rest":
    case "eighth_rest":
    case "sixteenth_rest":
      return 0;
    default:
      return 0;
  }
}

function paletteYOffset(symbol) {
  if (symbol.indexOf("rest")!=-1) return -10;
  switch (symbol) {
    case "whole_rest":
      return 10;
    case "half_rest":
      return 6;
    case "quarter_rest":
    case "eighth_rest":
    case "sixteenth_rest":
      return 0;
    default:
      return 0;
  }
}

function RestStaffLine({ symbol }) {
  if (symbol !== "half_rest" && symbol !== "whole_rest") return null;

  const y = symbol === "whole_rest" ? 0 : 0;

  return (
    <line
      x1={-6}
      x2={18}
      y1={y}
      y2={y}
      stroke="#000"
      strokeWidth={1.5}
    />
  );
}

